import { memo, useCallback, useMemo } from 'react';
import { Table, Tooltip } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

import {
  selectColumnExamples,
  selectMappingErrors,
  selectNotImported,
  selectTargetOwner,
  useImportWizard,
} from '../../../../features/members-import/state';
import { IMPORT_TARGETS } from '../../constants';
import MappingFooterNote from './MappingFooterNote.jsx';
import MappingRequiredBanner from './MappingRequiredBanner.jsx';
import MappingTargetSelect from './MappingTargetSelect.jsx';
import NotImportedList from './NotImportedList.jsx';

const EXAMPLE_SEPARATOR = ' · ';

/**
 * Step 2: map each file column to a system field. The mapping lives in the wizard state (the
 * reducer automaps once when the step first opens); this step only reads it and dispatches.
 */
function ImportMatchStep() {
  const { t } = useTranslation('people-access');
  const { state, setMapping } = useImportWizard();

  const errors = useMemo(() => selectMappingErrors(state), [state]);
  const notImported = useMemo(() => selectNotImported(state), [state]);
  // Only the file and its rows: picking a target must not rescan the whole sheet.
  const { file, rawRows } = state;
  const examples = useMemo(() => selectColumnExamples({ file, rawRows }), [file, rawRows]);
  const owners = useMemo(
    () => Object.fromEntries(IMPORT_TARGETS.map(({ id }) => [id, selectTargetOwner(state, id)])),
    [state],
  );

  const renderTarget = useCallback(
    (_, row) => (
      <div className="py-1.5">
        <MappingTargetSelect
          column={row.column}
          target={row.target}
          owners={owners}
          onChange={setMapping}
        />
      </div>
    ),
    [owners, setMapping],
  );
  const renderColumn = useCallback(
    (value) => <span className="block py-1.5 font-semibold text-content">{value}</span>,
    [],
  );
  const renderExample = useCallback(
    (value) => <span className="block max-w-md truncate py-1.5 text-xs text-content-secondary">{value}</span>,
    [],
  );

  const columns = useMemo(
    () => [
      { key: 'column', label: t('import.match.columnInFile'), render: renderColumn },
      { key: 'example', label: t('import.match.example'), render: renderExample },
      {
        key: 'target',
        label: (
          <Tooltip content={t('import.match.importsAsHint')} size="compact" position="top">
            <span tabIndex={0}>{t('import.match.importsAs')}</span>
          </Tooltip>
        ),
        render: renderTarget,
      },
    ],
    [t, renderColumn, renderExample, renderTarget],
  );

  const data = useMemo(
    () =>
      state.file.headers.map((column, index) => ({
        id: column,
        column,
        example: examples[index].join(EXAMPLE_SEPARATOR),
        target: state.mapping[column],
      })),
    [state.file.headers, state.mapping, examples],
  );

  return (
    <div className="flex max-w-[900px] flex-col gap-4">
      <MappingRequiredBanner errors={errors} />
      <div className="overflow-hidden rounded-xl border border-edge">
        <Table columns={columns} data={data} caption={t('import.match.caption')} />
      </div>
      <MappingFooterNote />
      <NotImportedList columns={notImported} />
    </div>
  );
}

export default memo(ImportMatchStep);
